import { useState } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import Text from '../../../shared/Text';
import Tooltip from '../../../shared/Tooltip/Tooltip.jsx';

import { useCreateCampaignPreviewMutation } from './CampaignsTable/hooks/useCampaignMutation';

export const ReviewLinkModal = ({ onClose }) => {
  const { t } = useTranslation('campaigns');
  const params = useParams();
  const { campaignId } = params;
  const [copied, setCopied] = useState(false);
  const { createCampaignPreviewMutation } = useCreateCampaignPreviewMutation(
    campaignId,
  );
  const linkPlaceHolder = `https://${
    import.meta.env.VITE_APP_HOST
  }/campaign_previews/...`;

  const handleCopy = () => {
    const reviewLinkUrl = `https://${
      import.meta.env.VITE_APP_HOST
    }/campaign_previews/${campaignId}`;

    navigator.clipboard
      .writeText(reviewLinkUrl)
      .then(() => {
        createCampaignPreviewMutation(campaignId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  return (
    <Wrapper>
      <Tooltip
        id="copyTooltip"
        getContent={() =>
          copied
            ? t('edit.reviewLinkModal.success')
            : t('edit.reviewLinkModal.hover')
        }
      />
      <HeaderWrapper>
        <Text as="div" variant="h5">
          {t('edit.reviewLinkModal.header')}
        </Text>
        <CloseIcon onClick={onClose} />
      </HeaderWrapper>
      <BodyWrapper>
        <Text as="div" variant="body1">
          {t('edit.reviewLinkModal.body')}
        </Text>
      </BodyWrapper>

      <ReviewLinkWrapper>
        <Text color="black.80" variant="body2">
          {t('edit.previewActions.internalReviewLink')}
        </Text>
        <LinkContainer>
          <LinkInput readOnly placeholder={linkPlaceHolder} />
          <CopyLink
            variant="small1"
            color="primary.100"
            onClick={handleCopy}
            data-tip
            data-for="copyTooltip"
          >
            {t('edit.reviewLinkModal.action')}
          </CopyLink>
        </LinkContainer>
      </ReviewLinkWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  padding: 4px;
`;

const HeaderWrapper = styled.div`
  width: 100%;
  text-align: left;
  display: flex;
  justify-content: space-between;
`;

const BodyWrapper = styled.div`
  text-align: left;
  margin-top: 20px;
`;

const ReviewLinkWrapper = styled.div`
  margin-top: 20px;
`;

const LinkContainer = styled.div`
  position: relative;
`;

const LinkInput = styled.input`
  width: 100%;
  padding: 9px 16px;
  margin: 10px 0;
  border-radius: 8px;
  border: 1px solid;
  border-color: ${themeGet('colors.black.60')};
`;

const CopyLink = styled(Text)`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
`;

const CloseIcon = styled.i.attrs({
  className: 'fa-regular fa-x',
})`
  cursor: pointer;
`;
